/**
 * Rebel Tide Hoodies — the offer layer (hoodie % off, packs, free shipping), on top of rt.js.
 * Loaded once as a module, right after rt.js (it uses RT.money, RT.addToCart, RT.loadPackData).
 *
 *  - <rt-offer-calc>: an offer number (price, saving or %) in the visitor's currency.
 *  - [data-rt-ship-min]: "Free shipping" notes, hidden when the price is under the free
 *    shipping threshold of the visitor's currency.
 *  - [data-rt-open-pack]: "2 hoodies = 50% OFF → Choose your 2nd hoodie" opens the Rebel Pack
 *    builder of the product page.
 *  - <rt-tee-upsell>: "Add an oversized tee" checkbox right above Add to cart. When it is ticked,
 *    Add to cart puts the hoodie and the chosen tee in the bag (the Surf Pack discount applies).
 *  - Hoodie pages: places "39% OFF" on the first photo and "Free shipping" in the sticky bar
 *    (templates rendered by the RT · Offer block).
 */

const RT = (window.RT = window.RT || {});
const cfg = RT.config || {};

const reduceMotion = () => document.documentElement.getAttribute('data-rt-motion') === 'off';

/** @param {'rebel'|'surf'} pack */
function packOff(pack) {
  const base = cfg.packs?.[pack]?.discountBase || 0;
  return RT.money ? RT.money.fromBase(base, { kind: 'amount' }) : base;
}

/**
 * Offer numbers in the visitor's currency (cents).
 * @param {number} a - hoodie price
 * @param {number} ac - hoodie compare-at price
 * @param {number} b - tee price
 */
function offerNumbers(a, ac, b) {
  const compareA = ac > a ? ac : a;
  const rebelTotal = Math.max(0, a * 2 - packOff('rebel'));
  const rebelWas = compareA * 2;
  const surfTotal = b > 0 ? Math.max(0, a + b - packOff('surf')) : 0;
  const surfWas = b > 0 ? compareA + b : 0;
  return {
    'hoodie-pct': compareA > a ? Math.round(((compareA - a) * 100) / compareA) : 0,
    'rebel-total': rebelTotal,
    'rebel-was': rebelWas,
    'rebel-each': Math.round(rebelTotal / 2),
    'rebel-pct': rebelWas > 0 && rebelTotal > 0 ? Math.round(((rebelWas - rebelTotal) * 100) / rebelWas) : 0,
    'surf-total': surfTotal,
    'surf-was': surfWas,
    'surf-save': Math.max(0, surfWas - surfTotal),
    'tee-extra': b > 0 ? Math.max(0, b - packOff('surf')) : 0,
  };
}

RT.offerNumbers = offerNumbers;

class RtOfferCalc extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  render() {
    const money = RT.money;
    if (!money || !this.hasAttribute('data-pending')) return;
    const numbers = offerNumbers(Number(this.dataset.a) || 0, Number(this.dataset.ac) || 0, Number(this.dataset.b) || 0);
    const value = numbers[/** @type {keyof typeof numbers} */ (this.dataset.calc || '')];
    if (value === undefined) return;
    this.textContent = this.dataset.template
      ? this.dataset.template.replace('[n]', String(value))
      : money.format(value, { trim: this.hasAttribute('data-trim') });
    this.removeAttribute('data-pending');
  }
}

/* ------------------------------------------------------------------ *
 * "Free shipping" notes
 * ------------------------------------------------------------------ */

function syncShipNotes(root = document) {
  const money = RT.money;
  if (!money) return;
  const threshold = money.fromBase(cfg.freeShipping?.base || 0, { kind: 'threshold' });
  root.querySelectorAll('[data-rt-ship-min]').forEach((el) => {
    const price = Number(/** @type {HTMLElement} */ (el).dataset.rtShipMin) || 0;
    /** @type {HTMLElement} */ (el).hidden = !(threshold > 0 && price >= threshold);
  });
}

/* ------------------------------------------------------------------ *
 * "Choose your 2nd hoodie" → opens the pack builder of the page
 * ------------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-rt-open-pack]') : null;
  if (!(trigger instanceof HTMLElement)) return;
  const scope = trigger.closest('.shopify-section, dialog') || document;
  const builder = scope.querySelector('rt-pack-builder[data-this-handle]') || document.querySelector('rt-pack-builder[data-this-handle]');
  if (!(builder instanceof HTMLElement)) return;
  event.preventDefault();
  const pack = trigger.dataset.rtOpenPack === 'surf' ? 'surf' : 'rebel';
  const tab = builder.querySelector(`[data-pack-tab="${pack}"]`);
  const isOpen = !builder.hasAttribute('data-collapsed') && /** @type {any} */ (builder).pack === pack;
  if (tab instanceof HTMLElement && !isOpen) tab.click();
  requestAnimationFrame(() => builder.scrollIntoView({ block: 'start', behavior: reduceMotion() ? 'auto' : 'smooth' }));
});

/* ------------------------------------------------------------------ *
 * <rt-tee-upsell> — "Add an oversized tee" above Add to cart
 * ------------------------------------------------------------------ */

class RtTeeUpsell extends HTMLElement {
  connectedCallback() {
    this.check = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-tee-check]'));
    this.panel = /** @type {HTMLElement|null} */ (this.querySelector('[data-tee-panel]'));
    this.picker = this.querySelector('rt-picker');
    this.thumb = /** @type {HTMLImageElement|null} */ (this.querySelector('[data-tee-thumb]'));
    this.check?.addEventListener('change', this.onToggle);
    this.addEventListener('rt:picker-change', this.onPick);
    if (this.check?.checked) this.onToggle();
  }

  disconnectedCallback() {
    this.check?.removeEventListener('change', this.onToggle);
    this.removeEventListener('rt:picker-change', this.onPick);
  }

  get active() {
    return Boolean(this.check?.checked);
  }

  /** The tee variant picked in the panel. */
  get teeVariant() {
    return /** @type {any} */ (this.picker)?.variant || null;
  }

  onToggle = () => {
    const on = this.active;
    this.classList.toggle('is-on', on);
    if (this.panel) this.panel.hidden = !on;
    this.showError('');
    if (!on || !this.picker || this.picker.dataset.ready) return;
    this.picker.dataset.ready = 'loading';
    RT.loadPackData()
      .then((/** @type {any} */ data) => {
        const picker = /** @type {any} */ (this.picker);
        picker.setup(data.tees || [], {
          label: this.dataset.pickLabel || cfg.strings?.choose || 'Choose',
          preferSize: this.hoodieSize() || 'M',
          preferModel: this.dataset.preferModel || '',
        });
        picker.dataset.ready = 'true';
      })
      .catch(() => {
        if (this.picker) delete this.picker.dataset.ready;
        this.showError(cfg.strings?.addFailed || '');
      });
  };

  /** @param {Event} event */
  onPick = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    if (this.thumb && detail.model?.image) this.thumb.src = detail.model.image;
    if (detail.variant) this.showError('');
  };

  /** Size picked for the hoodie, to suggest the same tee size. */
  hoodieSize() {
    const scope = this.closest('.shopify-section, dialog') || document;
    const checked = scope.querySelector('variant-picker input[type="radio"]:checked');
    return checked instanceof HTMLInputElement ? checked.value : '';
  }

  /** @param {string} message */
  showError(message) {
    const el = this.querySelector('[data-tee-error]');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = message;
    el.hidden = !message;
  }
}

// Runs before Horizon's own submit handler (registered on document, capture phase).
window.addEventListener(
  'submit',
  async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    const upsell = form.querySelector('rt-tee-upsell');
    if (!(upsell instanceof RtTeeUpsell) || !upsell.active) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const tee = upsell.teeVariant;
    if (!tee) {
      upsell.showError(cfg.strings?.chooseSize || 'Choose a size');
      upsell.scrollIntoView({ block: 'nearest', behavior: reduceMotion() ? 'auto' : 'smooth' });
      return;
    }
    const hoodieId = /** @type {HTMLInputElement|null} */ (form.querySelector('input[name="id"]'))?.value;
    if (!hoodieId) return;
    const quantity = Math.max(1, Number(/** @type {HTMLInputElement|null} */ (form.querySelector('input[name="quantity"]'))?.value) || 1);
    const button = form.querySelector('[name="add"], .add-to-cart-button, button[type="submit"]');
    button?.classList.add('is-loading');
    button?.setAttribute('aria-busy', 'true');
    try {
      await RT.addToCart(
        [
          { id: hoodieId, quantity },
          { id: tee.id, quantity: 1 },
        ],
        /** @type {HTMLElement} */ (form.closest('product-form-component') || upsell),
        'product'
      );
    } catch (error) {
      upsell.showError(/** @type {any} */ (error)?.message || cfg.strings?.addFailed || '');
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-busy');
    }
  },
  true
);

/* ------------------------------------------------------------------ *
 * Hoodie pages: "39% OFF" on the first photo, "Free shipping" in the sticky bar
 * ------------------------------------------------------------------ */

/** @param {HTMLTemplateElement|null} template */
const fromTemplate = (template) => {
  const node = template?.content.firstElementChild;
  return node ? /** @type {HTMLElement} */ (node.cloneNode(true)) : null;
};

function placeOfferBits(root = document) {
  root.querySelectorAll('.rt-deal').forEach((deal) => {
    const scope = deal.closest('.shopify-section, dialog') || document;
    const badge = /** @type {HTMLTemplateElement|null} */ (deal.querySelector('template[data-rt-media-badge]'));
    if (badge) {
      scope.querySelectorAll('media-gallery').forEach((gallery) => {
        [gallery.querySelector('slideshow-slide'), gallery.querySelector('.media-gallery__grid > li')].forEach((first) => {
          if (!first || first.querySelector(':scope > .rt-media-badge')) return;
          const node = fromTemplate(badge);
          if (node) first.prepend(node);
        });
      });
    }
    const ship = /** @type {HTMLTemplateElement|null} */ (deal.querySelector('template[data-rt-sticky-ship]'));
    if (ship) {
      document.querySelectorAll('.sticky-add-to-cart__price').forEach((price) => {
        if (price.querySelector('.rt-sticky-ship')) return;
        const node = fromTemplate(ship);
        if (node) price.appendChild(node);
      });
    }
  });
}

/* ------------------------------------------------------------------ *
 * Register + refresh
 * ------------------------------------------------------------------ */

const define = (/** @type {string} */ name, /** @type {CustomElementConstructor} */ ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

define('rt-offer-calc', RtOfferCalc);
define('rt-tee-upsell', RtTeeUpsell);

const refresh = (root = document) => {
  placeOfferBits(root);
  root.querySelectorAll('rt-offer-calc').forEach((el) => /** @type {RtOfferCalc} */ (el).render?.());
  syncShipNotes(document);
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => refresh());
else refresh();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) refresh(event.target);
});

// Horizon re-renders the product details when another size or colour is picked.
document.addEventListener('shopify:product:select', (event) => {
  /** @type {any} */ (event).promise
    ?.then(() => requestAnimationFrame(() => refresh()))
    .catch(() => {});
});
document.addEventListener('shopify:cart:lines-update', (event) => {
  /** @type {any} */ (event).promise
    ?.then(() => requestAnimationFrame(() => refresh()))
    .catch(() => {});
});
